import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export interface DatabaseField {
  name: string;
  label: string;
  required: boolean;
  type: 'text' | 'number' | 'email' | 'url' | 'date' | 'boolean';
  description?: string;
  validation?: (value: any) => boolean;
  transform?: (value: any) => any;
  isCustom?: boolean;
}

export interface ColumnMapping {
  csvColumn: string;
  dbField: string | null;
  isRequired: boolean;
  dataType: string;
  sample: string;
  confidence: 'exact' | 'fuzzy' | 'none';
}

// TAM membership fields (stored on tam_memberships table, not companies)
// These are valid mapping targets but handled separately by the edge function
const TAM_MEMBERSHIP_FIELDS: DatabaseField[] = [
  { name: 'tam_tier', label: 'TAM Tier', required: false, type: 'text', description: 'Total Addressable Market tier (Tier 1, Tier 2, etc.)' },
  { name: 'priority_score', label: 'Priority Score', required: false, type: 'number', description: 'Priority score (0-100)',
    transform: (value: any) => { if (!value) return 0; const n = parseInt(String(value)); return isNaN(n) ? 0 : Math.max(0, Math.min(100, n)); } },
  { name: 'account_owner', label: 'Account Owner', required: false, type: 'text', description: 'Person responsible for this account' },
  { name: 'account_status', label: 'Account Status', required: false, type: 'text', description: 'Current account status (Active, Inactive, etc.)' },
  { name: 'lead_source', label: 'Lead Source', required: false, type: 'text', description: 'How this lead was acquired' },
  { name: 'is_customer', label: 'Is Customer', required: false, type: 'boolean', description: 'Whether this company is already a customer',
    transform: (value: any) => { if (typeof value === 'boolean') return value; return ['true', 'yes', '1', 'customer'].includes(String(value).toLowerCase()); } },
  { name: 'in_sales_cycle', label: 'In Sales Cycle', required: false, type: 'boolean', description: 'Whether this company is in an active sales cycle',
    transform: (value: any) => { if (typeof value === 'boolean') return value; return ['true', 'yes', '1', 'active'].includes(String(value).toLowerCase()); } },
  { name: 'notes', label: 'Notes', required: false, type: 'text', description: 'Additional notes about the account' },
  { name: 'tags', label: 'Tags', required: false, type: 'text', description: 'Comma-separated tags' },
];

// Field-specific transforms applied after the schema is fetched
// These handle normalization for fields that need it (URLs, numbers, etc.)
const FIELD_TRANSFORMS: Record<string, (value: any) => any> = {
  'primary_domain': (value: string) => {
    if (!value) return null;
    let d = value.toLowerCase().trim();
    d = d.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0].split(':')[0];
    return d;
  },
  'linkedin_url': (value: string) => {
    if (!value) return null;
    let u = value.toLowerCase().trim();
    u = u.replace(/^https?:\/\//, '').replace(/^www\./, '').split('?')[0].replace(/\/$/, '');
    if (!u.startsWith('linkedin.com')) u = `linkedin.com/company/${u}`;
    return u;
  },
  'annual_revenue': (value: any) => {
    if (!value) return null;
    const n = parseFloat(String(value).replace(/[^\d.-]/g, '')); return isNaN(n) ? null : n;
  },
  'estimated_sales': (value: any) => {
    if (!value) return null;
    const n = parseFloat(String(value).replace(/[^\d.-]/g, '')); return isNaN(n) ? null : n;
  },
  'estimated_visits': (value: any) => {
    if (!value) return null;
    const n = parseInt(String(value).replace(/[^\d]/g, '')); return isNaN(n) ? null : n;
  },
  'founded_year': (value: any) => {
    if (!value) return null;
    const y = parseInt(String(value)); return isNaN(y) || y < 1800 || y > 2100 ? null : y;
  },
};

// Legacy static export — kept for backwards compatibility but prefer fetchCompanySchema()
export const ACCOUNT_DB_FIELDS: DatabaseField[] = TAM_MEMBERSHIP_FIELDS;

// Smart column matching using fuzzy logic
export function generateSmartMappings(csvHeaders: string[], dbFields: DatabaseField[]): ColumnMapping[] {
  const mappings: ColumnMapping[] = [];
  const usedFields = new Set<string>();

  csvHeaders.forEach(header => {
    const normalizedHeader = header.toLowerCase().trim();
    let bestMatch: string | null = null;
    let bestScore = 0;
    let confidence: 'exact' | 'fuzzy' | 'none' = 'none';

    // Try exact matches first
    for (const field of dbFields) {
      if (usedFields.has(field.name)) continue;
      const variations = getFieldVariations(field.name);
      if (variations.includes(normalizedHeader)) {
        bestMatch = field.name;
        bestScore = 1;
        confidence = 'exact';
        break;
      }
    }

    // If no exact match, try custom field name match (cf_video_count matches "video_count")
    if (!bestMatch) {
      for (const field of dbFields) {
        if (usedFields.has(field.name)) continue;
        // Check if header matches a cf_ field's suffix (e.g., "video_count" matches "cf_video_count")
        if (field.name.startsWith('cf_') && field.name.slice(3) === normalizedHeader.replace(/\s+/g, '_')) {
          bestMatch = field.name;
          bestScore = 0.95;
          confidence = 'exact';
          break;
        }
        // Check if header matches the field label exactly (case-insensitive)
        if (field.label.toLowerCase() === normalizedHeader) {
          bestMatch = field.name;
          bestScore = 0.95;
          confidence = 'exact';
          break;
        }
      }
    }

    // If still no match, try fuzzy matching
    if (!bestMatch) {
      for (const field of dbFields) {
        if (usedFields.has(field.name)) continue;
        const score = calculateSimilarity(normalizedHeader, field.name);
        // Also check against label
        const labelScore = calculateSimilarity(normalizedHeader, field.label.toLowerCase());
        const maxScore = Math.max(score, labelScore);
        if (maxScore > bestScore && maxScore > 0.6) { // 60% similarity threshold
          bestMatch = field.name;
          bestScore = maxScore;
          confidence = 'fuzzy';
        }
      }
    }

    // Mark used to prevent duplicate auto-mappings
    if (bestMatch) {
      usedFields.add(bestMatch);
    }

    const dbField = dbFields.find(f => f.name === bestMatch);
    mappings.push({
      csvColumn: header,
      dbField: bestMatch,
      isRequired: dbField?.required || false,
      dataType: dbField?.type || 'text',
      sample: '',
      confidence,
    });
  });

  return mappings;
}

// Get common variations for a database field name
function getFieldVariations(fieldName: string): string[] {
  const variations: { [key: string]: string[] } = {
    'company_name': ['company_name', 'company name', 'name', 'company', 'business name', 'organization', 'organisation', 'account name', 'employer'],
    'domain': ['domain', 'website_domain', 'web domain', 'company domain', 'website_url', 'website url', 'website', 'url', 'web address', 'web url', 'site url', 'site', 'web site', 'company website', 'normalizedurl', 'normalized url', 'normalized domain', 'merge domain', 'company domain (normalized url)'],
    'linkedin_url': ['linkedin_url', 'linkedin url', 'linkedin', 'company linkedin', 'li url', 'linkedin page', 'linkedin profile', 'linkedin company url', 'consolidated linkedin url', 'consolidated company linkedin', 'cleaned linkedin url'],
    'industry': ['industry', 'sector', 'vertical', 'business type'],
    'employee_count': ['employee_count', 'employee count', 'employees', 'team size', 'staff count', 'headcount', 'number of employees', 'num employees'],
    'annual_revenue': ['annual_revenue', 'annual revenue', 'revenue', 'yearly revenue', 'annual sales'],
    'phone': ['phone', 'telephone', 'phone number', 'contact number', 'tel'],
    'address': ['address', 'street address', 'street', 'mailing address'],
    'city': ['city', 'town', 'city name'],
    'state': ['state', 'state/province', 'province'],
    'country': ['country', 'nation', 'country name', 'country code', 'hq country'],
    'postal_code': ['postal_code', 'postal code', 'zip', 'zip code', 'postcode', 'zip/postal code'],
    'description': ['description', 'about', 'company description', 'bio', 'summary', 'overview'],
    'logo_url': ['logo_url', 'logo url', 'logo', 'company logo', 'avatar', 'image url'],
    'founded_year': ['founded_year', 'founded year', 'founded', 'year founded', 'established', 'year established'],
    'ecommerce_platform': ['ecommerce_platform', 'ecommerce platform', 'platform', 'e-commerce platform', 'shop platform', 'store platform'],
    'tiktok_url': ['tiktok_url', 'tiktok url', 'tiktok', 'tik tok', 'tiktok profile', 'tiktok page'],
    'instagram_url': ['instagram_url', 'instagram url', 'instagram', 'ig url', 'ig', 'instagram profile'],
    'facebook_url': ['facebook_url', 'facebook url', 'facebook', 'fb url', 'fb', 'facebook page'],
    'tiktok_shop_id': ['tiktok_shop_id', 'tiktok shop id', 'tiktok shop', 'ts id', 'shop id', 'tiktokshop id'],
    'shop_name': ['shop_name', 'shop name', 'store name', 'storefront name', 'storefront'],
    'estimated_sales': ['estimated_sales', 'estimated sales', 'est sales', 'monthly sales', 'sales estimate'],
    'estimated_visits': ['estimated_visits', 'estimated visits', 'traffic', 'monthly traffic', 'visits', 'monthly visits', 'web traffic', 'site traffic', 'consolidated traffic', 'website traffic'],
    'total_gmv': ['total_gmv', 'total gmv', 'gmv total'],
    'total_video_count': ['total_video_count', 'total video count', 'total videos'],
    'total_creator_count': ['total_creator_count', 'total creator count', 'total creators'],
    'tiktok_category': ['tiktok_category', 'tiktok category', 'tt category'],
    'tam_tier': ['tam_tier', 'tam tier', 'tier', 'priority tier', 'market tier', 'icp tier'],
    'priority_score': ['priority_score', 'priority score', 'priority', 'score', 'ranking', 'lead score'],
    'account_owner': ['account_owner', 'account owner', 'owner', 'account manager', 'sales rep', 'assigned to', 'rep'],
    'account_status': ['account_status', 'account status', 'status'],
    'lead_source': ['lead_source', 'lead source', 'source', 'origin', 'channel', 'acquisition source'],
    'is_customer': ['is_customer', 'is customer', 'customer', 'existing customer', 'current customer'],
    'in_sales_cycle': ['in_sales_cycle', 'in sales cycle', 'sales cycle', 'active deal', 'pipeline'],
    'notes': ['notes', 'note', 'comments', 'comment', 'remarks'],
    'tags': ['tags', 'tag', 'labels', 'label', 'categories'],
    // Contact-specific field variations
    'first_name': ['first_name', 'first name', 'firstname', 'first', 'given name', 'givenname'],
    'last_name': ['last_name', 'last name', 'lastname', 'last', 'surname', 'family name', 'familyname'],
    'full_name': ['full_name', 'full name', 'fullname', 'name', 'display name', 'contact name'],
    'primary_email': ['primary_email', 'primary email', 'email', 'email address', 'e-mail', 'email_address', 'work email', 'business email', 'contact email', 'validated email', 'verified email'],
    'primary_phone': ['primary_phone', 'primary phone', 'phone', 'phone number', 'telephone', 'tel', 'mobile', 'cell', 'contact phone', 'work phone', 'direct phone'],
    'primary_linkedin': ['primary_linkedin', 'primary linkedin', 'linkedin url', 'linkedin', 'linkedin profile', 'li url', 'linkedin_url', 'personal linkedin'],
    'title': ['title', 'job title', 'job_title', 'position', 'role', 'designation', 'job role', 'official title', 'official_title'],
    'department': ['department', 'dept', 'team', 'division', 'business unit'],
    'seniority': ['seniority', 'seniority level', 'level', 'job level', 'career level', 'management level'],
    'source': ['source', 'lead source', 'lead_source', 'origin', 'channel', 'acquisition source'],
    'company_domain': ['company_domain', 'company domain', 'company website', 'website', 'domain', 'website_domain', 'web domain', 'website_url', 'website url', 'url', 'web address', 'web url', 'site url', 'site', 'web site', 'company domain (normalized url)', 'normalized url'],
    'company_linkedin': ['company_linkedin', 'company linkedin', 'company linkedin url', 'company li url', 'company linkedin page', 'company linkedin url'],
  };

  return variations[fieldName] || [fieldName];
}

// Calculate string similarity (Levenshtein distance based)
function calculateSimilarity(str1: string, str2: string): number {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;

  if (longer.length === 0) return 1.0;

  const distance = levenshteinDistance(longer, shorter);
  return (longer.length - distance) / longer.length;
}

function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));

  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const substitutionCost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // insertion
        matrix[j - 1][i] + 1, // deletion
        matrix[j - 1][i - 1] + substitutionCost // substitution
      );
    }
  }

  return matrix[str2.length][str1.length];
}

// Transform data according to field configuration
export function transformDataWithMapping(
  rawData: any[],
  mappings: ColumnMapping[],
  dbFields: DatabaseField[]
): any[] {
  return rawData.map((row, rowIndex) => {
    const transformedRow: any = {};

    mappings.forEach((mapping, colIndex) => {
      if (!mapping.dbField || !row[colIndex]) return;

      const dbField = dbFields.find(f => f.name === mapping.dbField);
      if (!dbField) return;

      let value = row[colIndex];

      // Skip empty/whitespace-only values
      if (typeof value === 'string' && value.trim() === '') return;

      // Apply transformation if defined
      if (dbField.transform) {
        try {
          value = dbField.transform(value);
        } catch (error) {
          console.warn(`Transform error for ${mapping.dbField} at row ${rowIndex + 1}:`, error);
        }
      }

      // Apply validation if defined
      if (dbField.validation && !dbField.validation(value)) {
        console.warn(`Validation failed for ${mapping.dbField} at row ${rowIndex + 1}:`, value);
        return;
      }

      transformedRow[mapping.dbField] = value;
    });

    return transformedRow;
  }).filter(row => Object.keys(row).length > 0);
}

// Fetch the full company schema directly from the DB via RPC
// This is the source of truth — no hardcoded columns, always in sync with the actual table
export async function fetchCompanySchema(): Promise<DatabaseField[]> {
  const { data, error } = await supabase.rpc('get_company_schema');

  if (error || !data) {
    console.warn('Failed to fetch company schema via RPC:', error?.message);
    // Fallback: return just the TAM fields so the UI isn't completely empty
    return [...TAM_MEMBERSHIP_FIELDS];
  }

  const schemaFields: DatabaseField[] = (data as any[]).map((row: any) => {
    const fieldType = row.data_type as DatabaseField['type'];
    // Map DB column_name to the field name used in the upload pipeline
    // 'name' -> 'company_name', 'primary_domain' -> 'domain' for backwards compat
    let fieldName = row.column_name;
    let fieldLabel = row.display_label;
    if (fieldName === 'name') { fieldName = 'company_name'; fieldLabel = 'Company Name'; }
    if (fieldName === 'primary_domain') { fieldName = 'domain'; fieldLabel = 'Website / Domain'; }

    return {
      name: fieldName,
      label: fieldLabel,
      required: row.is_required || false,
      type: fieldType,
      description: row.description || '',
      isCustom: row.is_custom || false,
      // Attach field-specific transforms if defined
      ...(FIELD_TRANSFORMS[row.column_name] ? { transform: FIELD_TRANSFORMS[row.column_name] } : {}),
      // Generic transforms for number/boolean types without a specific transform
      ...(!FIELD_TRANSFORMS[row.column_name] && fieldType === 'number' ? {
        transform: (value: any) => {
          if (!value) return null;
          const n = parseFloat(String(value).replace(/[^\d.-]/g, ''));
          return isNaN(n) ? null : n;
        }
      } : {}),
      ...(!FIELD_TRANSFORMS[row.column_name] && fieldType === 'boolean' ? {
        transform: (value: any) => {
          if (typeof value === 'boolean') return value;
          return ['true', 'yes', '1'].includes(String(value).toLowerCase());
        }
      } : {}),
    };
  });

  // Append TAM membership fields (stored on tam_memberships, not companies, but valid mapping targets)
  return [...schemaFields, ...TAM_MEMBERSHIP_FIELDS];
}

// Legacy alias — fetchCustomFields now delegates to fetchCompanySchema
export async function fetchCustomFields(): Promise<DatabaseField[]> {
  return fetchCompanySchema();
}

// Merge static fields with dynamically fetched fields
// When schema is fetched via fetchCompanySchema(), it already includes everything,
// so this just returns the fetched fields. Kept for backwards compatibility.
export function mergeFieldLists(customFields: DatabaseField[]): DatabaseField[] {
  // If customFields already contains standard fields (from fetchCompanySchema), return as-is
  if (customFields.some(f => f.name === 'company_name' || f.name === 'domain')) {
    return customFields;
  }
  // Legacy fallback: merge static ACCOUNT_DB_FIELDS with custom fields
  return [...ACCOUNT_DB_FIELDS, ...customFields];
}

// Convert a display label to a cf_ prefixed column name (for preview in UI)
export function sanitizeColumnName(label: string): string {
  let name = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return `cf_${name}`.slice(0, 63);
}

// Validate mappings before processing
export function validateMappings(mappings: ColumnMapping[], dbFields: DatabaseField[]): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check required fields
  const requiredFields = dbFields.filter(f => f.required);
  const mappedFields = mappings.filter(m => m.dbField).map(m => m.dbField);
  const mappedRequiredFields = mappedFields.filter(f => requiredFields.some(rf => rf.name === f));

  const missingRequired = requiredFields
    .filter(rf => !mappedRequiredFields.includes(rf.name))
    .map(rf => rf.label);

  if (missingRequired.length > 0) {
    errors.push(`Missing required fields: ${missingRequired.join(', ')}`);
  }

  // Check for duplicate mappings
  const usedFields = mappings.filter(m => m.dbField).map(m => m.dbField);
  const duplicates = usedFields.filter((field, index) => usedFields.indexOf(field) !== index);

  if (duplicates.length > 0) {
    const dupLabels = [...new Set(duplicates)].map(d => {
      const field = dbFields.find(f => f.name === d);
      return field ? field.label : d;
    });
    errors.push(`Duplicate field mappings: ${dupLabels.join(', ')}. Only one CSV column can map to each CRM field.`);
  }

  // Suggest mapping domain or linkedin for better dedup
  const hasIdentifier = mappedFields.includes('domain') || mappedFields.includes('linkedin_url');
  if (!hasIdentifier && mappedRequiredFields.length > 0) {
    warnings.push('Consider mapping Domain or LinkedIn URL for better deduplication');
  }

  // Check for unmapped columns
  const unmappedCount = mappings.filter(m => !m.dbField).length;
  if (unmappedCount > 0) {
    warnings.push(`${unmappedCount} column${unmappedCount > 1 ? 's' : ''} will be ignored`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}
